import React, {useCallback, useEffect, useState} from 'react';
import Dialog from "../../../components/Dialog";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  format,
  isAfter,
  isBefore,
  parse
} from "date-fns";
import {DayPicker} from "react-day-picker";
import {getDensityQuery} from "../../../api/firebaseApi";
import {
  calcEndDate,
  formatDate,
  formatDate as formatDate_,
  getInstanceName,
  isDev
} from "../../Util";
import useFirestoreGetDocs from "../../../hooks/useFirestoreGetDocs";
import {useDispatch, useSelector} from "react-redux";
import {selectApp} from "../../app/appSlice";
import _ from "lodash";
import {parseDate} from "../reservationUtil";
import {doBook, selectSession} from "../sessionSlice";
import {getRoomRateItemFromItem, trackEvent} from "../../../api/googleTag";
import ToolTip from "../../../components/ToolTip";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Checkbox from "../../../components/Checkbox";

const startMonth = new Date();
const ARRIVAL_DATE = 0;
const DEPART_DATE = 1;

export function ItineraryDateRangePicker({item, isCriteriaItem, disabled}) {

  const {reservation, bookBusy, site, customer} = useSelector(state => ({
    reservation: selectSession(state).reservation,
    bookBusy: selectSession(state).bookBusy,
    customer: selectApp(state).customer,
    site: selectApp(state).site,
  }));

  const dispatch = useDispatch();
  const book = useCallback((params) => dispatch(doBook(params)),
    [dispatch]);

  const {fetchData: fetchDensity, data, busy} = useFirestoreGetDocs();

  const [open, setOpen] = useState(false)
  const [range, setRange] = useState(undefined);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [arrivalDate, setArrivalDate] = useState(undefined)
  const [departDate, setDepartDate] = useState(undefined)
  const [nextClickType, setNextClickType] = useState(ARRIVAL_DATE)
  const [month, setMonth] = useState(startMonth);
  const [disabledDays, setDisabledDays] = useState([{before: new Date()}])
  const [notAvailableDays, setNotAvailableDays] = useState([])
  const [queryTimer, setQueryTimer] = useState(0)
  const [error, setError] = useState(undefined)
  const [dayUse, setDayUse] = useState(site?.allowSameDay)

  useEffect(() => {
    if (open) {
      const startDate = parseDate(item.startDate)
      const endDate = parseDate(item.endDate)

      setRange(
        {from: new Date(startDate), to: new Date(endDate)});

      setArrivalDate(startDate);
      setDepartDate(endDate)

      handleOnMonthChange(month, customer, item.propertyId, item.inventoryId)
    }
  }, [open, item]);

  const handleOnSelect = React.useCallback((range) => {

    if(!dayUse) {
      if (nextClickType === ARRIVAL_DATE) {
        setNextClickType(DEPART_DATE)

        if (range?.from && isBefore(new Date(range.from.toDateString()),
          new Date(arrivalDate.toDateString()))) {

          setArrivalDate(new Date(range.from.toDateString()));

          setRange({
            from: range.from,
            to: undefined,
          })

        } else {
          if (range?.to) {
            setArrivalDate(new Date(range.to.toDateString()));

            setRange({
              from: range.to,
              to: undefined,
            })
          }
        }
      } else {
        setNextClickType(ARRIVAL_DATE)
        if (range?.to) {
          setDepartDate(new Date(range.to.toDateString()));
          setArrivalDate(new Date(range.from.toDateString()));

          setRange({
            from: range.from,
            to: range.to
          })
        }
      }
    } else {
      setSelectedDate(range)

      setDepartDate(addDays(range, 1));
      setArrivalDate(range);
    }

  }, [dayUse,
    setRange,
    setArrivalDate,
    setDepartDate,
    arrivalDate,
    nextClickType,
    setNextClickType]);

  const handleOnDayClick = useCallback((dayClicked) => {

    if (notAvailableDays && notAvailableDays.length > 0) {
      let dayBefore = notAvailableDays.toReversed().find(
        notAvailableDay => isBefore(notAvailableDay, dayClicked));
      const dayAfter = notAvailableDays.find(
        notAvailableDay => isAfter(notAvailableDay, dayClicked));

      if (dayBefore) {
        dayBefore = addDays(dayBefore, 1);
      }

      setDisabledDays(
        [{before: dayBefore ?? new Date()}, {after: dayAfter}])

      if (nextClickType === DEPART_DATE) {
        setDisabledDays([{before: new Date()}, ...notAvailableDays]);
      }
    }
  }, [notAvailableDays, nextClickType])

  const Footer = ({arrivalDate, departDate, dayUse}) => {

    if (arrivalDate) {
      if(!dayUse) {
        const duration = Math.abs(
          differenceInCalendarDays(arrivalDate, departDate))

        return (
          <div className={"bn-date-picker-footer"}>
            <h1>{`${duration} ${duration > 1 ? 'Nights' : 'Night'}`}</h1>
            <p>
              {format(arrivalDate, 'PPP')} – {format(departDate, 'PPP')}
            </p>
          </div>
        )
      }
      else {
        return (
          <div className={"bn-date-picker-footer"}>
            <p>
              {format(arrivalDate, 'PPP')}
            </p>
          </div>
        )
      }
    }
  }

  const handleOnMonthChange = (month, customer, propertyId, inventoryId) => {
    clearTimeout(queryTimer);

    const timeout = setTimeout(() => {

      fetchDensity(
        getDensityQuery(isDev() ? 'demo' : getInstanceName(customer.api_url),
          propertyId,
          inventoryId,
          formatDate_(month, 'yyyy-MM-dd'),
          formatDate_(addMonths(month, 2), 'yyyy-MM-dd')),
        (data) => {
          const notAvailableDays = calcNotAvailableDays(data);
          setNotAvailableDays(notAvailableDays);
          setDisabledDays([{before: new Date()}, ...notAvailableDays])
        })

    }, 500);

    setQueryTimer(timeout)

    setMonth(month)
  }

  const calcNotAvailableDays = (data) => {
    const groupBy = _.groupBy(data, 'date');
    const sum = Object.keys(groupBy).map(date => {
      return {
        [date]: _.sumBy(groupBy[date], 'roomsRemaining')
      }
    })
    const filter = sum.filter(s => (s[Object.keys(s)[0]] < 1));
    return filter.map(f => (parseDate(Object.keys(f)[0])));
  }

  const handleUpdate = (reservation, open) => {
    setError(undefined);

    trackEvent("edit_cart", "clicked", "edit_itinerary",
      {items: [getRoomRateItemFromItem(item, item.amountInclTax)]});

    book({
      reservation, reserve: {
        ...item,
        startDate: formatDate(arrivalDate, 'yyyy-MM-dd'),
        endDate: formatDate(departDate, 'yyyy-MM-dd'),
        rateOverridden: false
      }
    }).unwrap().then((r) => {
      setOpen(!open)
    }).catch(error => {
      setError("Selected dates are not available.")
      console.log('update stay dates error', error)
    })
  }

  const handleDayUseClicked = (checked, site) => {
    setDayUse(checked)

    if(checked) {
      setDepartDate(new Date(arrivalDate.toDateString()))

      setSelectedDate(arrivalDate)
    }
    else {
      setDepartDate(addDays(arrivalDate, site?.minDuration))

      setRange({
        from: arrivalDate,
        to: addDays(arrivalDate, site?.minDuration)
      })
    }

  }

  const canSave = isAfter(departDate, arrivalDate);

  return (
    <div>
      <div className={"bn-itinerary-dates-container"}>
        <div className={"bn-itinerary-checkin-checkout-container"}>
          <div className={"startdate-container"}>
            <h4>Check-In</h4>
            <span>{item?.startDate ? parse(item.startDate, 'yyyy-MM-dd',
              new Date()).toLocaleDateString() : ''}</span>
          </div>
          <div className={"enddate-container"}>
            <h4>Check-Out</h4>
            <span>{item?.endDate && calcEndDate(item) ? calcEndDate(item).toLocaleDateString() : ''}</span>
          </div>
        </div>

        {(!isCriteriaItem && !disabled) &&
          <ToolTip content={'Edit your stay dates'}>
            <div className={"bn-itinerary-edit-button"}
                 onClick={() => setOpen(!open)}>
              <FontAwesomeIcon icon="fa-solid fa-pen" />
            </div>
          </ToolTip>
        }
      </div>

      <Dialog portalId={"bn-itinerary-datepicker-dialog"} open={open}
              onOpenChange={setOpen}>
        <>
          <div className={"bn-dialog-content"}>
            <div className={"bn-dialog-text"}>
              <p>Changing your stay might affect your rate.</p>
            </div>

            <div className={'bn-date-picker-months-scroll'}>
              <DayPicker
                mode={!dayUse ? "range" : "single"}
                numberOfMonths={2}
                selected={!dayUse ? range : selectedDate}
                onSelect={handleOnSelect}
                onDayClick={handleOnDayClick}
                fromMonth={startMonth}
                disabled={disabledDays}
                onMonthChange={(month) => handleOnMonthChange(month, customer,
                  item.propertyId, item.inventoryId)}
              />
            </div>

            <Footer arrivalDate={arrivalDate} departDate={departDate} dayUse={dayUse}/>

            {site?.allowSameDay && <div className={'bn-date-picker-day-use-container'}>
              <Checkbox id={'dayUse'}
                        value={dayUse}
                        onChange={(event) => handleDayUseClicked(
                          event.target.checked, site)}
                        className={'bn-checkbox-item'}
                        label={"Day Use"}
              />
            </div>}

            {error && <div className={"bn-error-message"}>{error}</div>}

            <div className={"bn-dialog-button-container"}>
              <button className={"bn-booknow-button"}
                      type={"button"}
                      name={"booknow-update-button"}
                      onClick={() => handleUpdate(reservation, open)}
                      disabled={busy || bookBusy || !canSave}
              >
                Save Changes
              </button>
              <button className={"bn-booknow-button bn-secondary-button"}
                      type={"button"}
                      name={"booknow-close-button"}
                      onClick={() => setOpen(!open)}
                      disabled={bookBusy}
              >
                Close
              </button>
            </div>
          </div>
        </>
      </Dialog>
    </div>
  )
}

